/**
 * EventsHero — encabezado premium de la página de Eventos (`/eventos`).
 *
 * Reusa el MISMO lenguaje visual del hero del Home (`HomeHero`): superficie
 * oscura con imagen REAL de público en concierto (del catálogo) difuminada y
 * **fundida** con el fondo (blur + duotono azul→violeta→cian en `overlay` +
 * scrim de legibilidad + glows + grid). Es un header de página (más compacto
 * que el hero del landing): eyebrow + título + subtítulo reales, sin CTAs.
 *
 * Local a Eventos — no toca `HomeHero` ni el Home. Recibe el copy real por
 * props (i18n del namespace `events`) y la imagen de fondo ya elegida.
 */

interface EventsHeroProps {
  title: string;
  subtitle: string;
  /** Imagen de fondo (dato real: portada representativa del catálogo). */
  heroImage?: string | null;
}

export function EventsHero({ title, subtitle, heroImage = null }: EventsHeroProps): JSX.Element {
  return (
    <section className="relative isolate flex min-h-[280px] items-center overflow-hidden bg-[#0a0f1f] text-white md:min-h-[340px]">
      {/* Backdrop — imagen real de concierto, a sangre, difuminada y fundida. */}
      {heroImage && (
        <img
          src={heroImage}
          alt=""
          aria-hidden="true"
          loading="eager"
          decoding="async"
          className="pointer-events-none absolute inset-0 -z-20 h-full w-full scale-105 object-cover object-[center_35%] opacity-[0.5]"
          style={{ filter: 'blur(2px) saturate(1.15)' }}
        />
      )}

      {/* Duotono azul→violeta→cian sobre la foto (fusión con la paleta). */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            'linear-gradient(120deg, rgba(0,118,244,0.48) 0%, rgba(124,58,237,0.44) 58%, rgba(0,214,238,0.30) 100%)',
          mixBlendMode: 'overlay',
        }}
      />

      {/* Scrim de legibilidad — oscuro a la izquierda (texto) y hacia abajo. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            'linear-gradient(90deg, rgba(10,15,31,0.95) 0%, rgba(10,15,31,0.80) 36%, rgba(10,15,31,0.45) 66%, rgba(10,15,31,0.58) 100%),' +
            'linear-gradient(180deg, rgba(10,15,31,0.5) 0%, transparent 30%, transparent 58%, rgba(10,15,31,0.92) 100%)',
        }}
      />

      {/* Glows radiales (azul Mi Boleta → violeta → cian). */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            'radial-gradient(50% 90% at 6% 0%, rgba(0,118,244,0.42) 0%, transparent 60%),' +
            'radial-gradient(55% 90% at 99% 14%, rgba(124,58,237,0.40) 0%, transparent 62%),' +
            'radial-gradient(60% 90% at 84% 116%, rgba(0,214,238,0.20) 0%, transparent 60%)',
        }}
      />

      {/* Grid técnica muy sutil para textura. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 opacity-[0.12]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.12) 1px, transparent 1px),' +
            'linear-gradient(90deg, rgba(255,255,255,0.12) 1px, transparent 1px)',
          backgroundSize: '64px 64px',
          maskImage: 'radial-gradient(120% 100% at 28% 0%, #000 18%, transparent 78%)',
          WebkitMaskImage: 'radial-gradient(120% 100% at 28% 0%, #000 18%, transparent 78%)',
        }}
      />

      <div className="container-fc relative w-full py-12 md:py-16">
        <div className="max-w-2xl">
          <p className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-medium uppercase tracking-[0.2em] text-white/80 backdrop-blur">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-cyan opacity-75 motion-safe:animate-ping" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-cyan" />
            </span>
            Eventos
          </p>

          <h1 className="mt-4 font-display text-3xl font-bold leading-[1.05] tracking-tight drop-shadow-[0_2px_18px_rgba(0,0,0,0.45)] md:text-4xl lg:text-5xl">
            {title}
          </h1>
          <p className="mt-3 max-w-2xl text-base text-white/80 md:text-lg">{subtitle}</p>
        </div>
      </div>

      {/* Borde inferior suave hacia el canvas claro del listado. */}
      <div
        aria-hidden
        className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent"
      />
    </section>
  );
}
